"use client";

import { useEffect } from "react";
import { reportError } from "@/lib/error-reporting";

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    // Report error to error tracking service (e.g. Sentry)
    reportError(error, {
      context: "GlobalErrorBoundary",
      digest: error.digest,
    });
  }, [error]);

  // Note: In global-error, we cannot use next/link as the app shell may be broken
  // Using a plain anchor tag is intentional here for maximum reliability
  return (
    <html lang="en">
      <body>
        <div style={styles.container}>
          <div style={styles.content}>
            <div style={styles.icon}>🚨</div>
            <h1 style={styles.heading}>Critical Error</h1>
            <p style={styles.text}>
              The application encountered a critical error and could not
              recover. Please try refreshing the page.
            </p>
            {process.env.NODE_ENV === "development" && (
              <details style={styles.details}>
                <summary style={styles.summary}>Error Details</summary>
                <pre style={styles.pre}>{error.message}</pre>
                {error.stack && <pre style={styles.stack}>{error.stack}</pre>}
                {error.digest && <p>Error ID: {error.digest}</p>}
              </details>
            )}
            <div style={styles.actions}>
              <button
                onClick={reset}
                style={styles.primaryButton}
                type="button"
              >
                Try Again
              </button>
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
              <a href="/" style={styles.secondaryButton}>
                Go to Homepage
              </a>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem",
    fontFamily:
      'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: "#0a0a0a",
    color: "#ededed",
  },
  content: {
    textAlign: "center",
    maxWidth: "500px",
  },
  icon: {
    fontSize: "4rem",
    marginBottom: "1rem",
  },
  heading: {
    fontSize: "1.75rem",
    fontWeight: 600,
    marginBottom: "0.75rem",
    color: "#ef4444",
  },
  text: {
    color: "#a1a1aa",
    marginBottom: "1.5rem",
    lineHeight: 1.6,
  },
  details: {
    textAlign: "left" as const,
    marginBottom: "1.5rem",
    padding: "1rem",
    backgroundColor: "#18181b",
    borderRadius: "8px",
  },
  summary: {
    cursor: "pointer",
    fontWeight: 500,
    marginBottom: "0.5rem",
  },
  pre: {
    fontSize: "0.875rem",
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    marginTop: "0.5rem",
  },
  stack: {
    fontSize: "0.75rem",
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    marginTop: "0.5rem",
    color: "#71717a",
  },
  actions: {
    display: "flex",
    gap: "1rem",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  primaryButton: {
    padding: "0.75rem 1.5rem",
    borderRadius: "8px",
    fontWeight: 500,
    fontSize: "0.9375rem",
    cursor: "pointer",
    backgroundColor: "#ef4444",
    color: "white",
    border: "none",
    transition: "opacity 0.2s ease",
  },
  secondaryButton: {
    padding: "0.75rem 1.5rem",
    borderRadius: "8px",
    fontWeight: 500,
    fontSize: "0.9375rem",
    cursor: "pointer",
    backgroundColor: "transparent",
    color: "#e4e4e7",
    border: "1px solid #333",
    textDecoration: "none",
    display: "inline-block",
  },
};
